import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Dexie from 'dexie';

import {
  FaSearch,
  FaBuilding,
  FaEnvelope,
  FaMapMarkerAlt,
  FaMoneyBill,
  FaReceipt,
  FaCalendar,
  FaEye,
  FaSpinner,
  FaExclamationTriangle,
  FaSyncAlt,
  FaFileInvoiceDollar,
  FaChartBar,
  FaListAlt,
  FaChartLine,
  FaChartPie,
  FaTachometerAlt,
  FaArrowUp,
  FaArrowDown,
  FaPercent,
  FaSignal,
  FaBan,
  FaDatabase,
  FaClock,
  FaBars,
  FaTimes,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
  FaChevronLeft,
  FaChevronRight,
  FaLayerGroup,
} from "react-icons/fa";

import API_CONFIG from "../components/API_CONFIG/API_CONFIG";

// ========== CONFIGURATION DEXIE (IndexedDB) ==========
const db = new Dexie('InvoiceTableCacheADM');
db.version(1).stores({
  invoices: 'id, company_name, email, address, total_ttc, created_at, cacheId, userTel',
  cacheInfo: 'key',
  requestLog: '++id, timestamp, endpoint, userTel'
});

// ========== RATE LIMITER ==========
class RateLimiter {
  constructor(maxRequests = 5, timeWindow = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
    this.requests = [];
    this.requestQueue = [];
    this.isProcessing = false;
  }

  async acquire() {
    return new Promise((resolve) => {
      this.requestQueue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const now = Date.now();
      this.requests = this.requests.filter(time => now - time < this.timeWindow);

      if (this.requests.length < this.maxRequests) {
        const resolve = this.requestQueue.shift();
        this.requests.push(now);
        resolve();
      } else {
        const oldestRequest = this.requests[0];
        const waitTime = this.timeWindow - (now - oldestRequest);
        await new Promise(resolve => setTimeout(resolve, waitTime + 100));
      }
    }

    this.isProcessing = false;
  }
}

// ========== CONFIGURATION AXIOS ==========
const axiosInstance = axios.create({
  timeout: 15000,
});

axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config;
    if (!config || !config._retry) {
      return Promise.reject(error);
    }
    
    config._retryCount = config._retryCount || 0;
    
    if (config._retryCount >= config._retry) {
      return Promise.reject(error);
    }
    
    config._retryCount += 1;
    const delay = config._retryDelay ? config._retryDelay(config._retryCount) : 1000 * Math.pow(2, config._retryCount);
    await new Promise(resolve => setTimeout(resolve, delay));
    
    return axiosInstance(config);
  }
);

const rateLimiter = new RateLimiter(3, 60000);

// ========== FONCTIONS UTILITAIRES ==========
const logRequest = async (endpoint, userTel) => {
  try {
    await db.requestLog.add({
      endpoint,
      timestamp: new Date().toISOString(),
      userTel
    });
    
    const count = await db.requestLog.count();
    if (count > 100) {
      const oldest = await db.requestLog.orderBy('id').first();
      if (oldest) await db.requestLog.delete(oldest.id);
    }
  } catch (error) {
    console.error('Erreur logging:', error);
  }
};

const isCacheValid = async (userTel) => {
  try {
    const cacheInfo = await db.cacheInfo.get(`invoices_data_${userTel}`);
    if (!cacheInfo) return false;
    const now = new Date();
    const cacheDate = new Date(cacheInfo.timestamp);
    const hoursDiff = (now - cacheDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  } catch (error) {
    console.error('Erreur cache:', error);
    return false;
  }
};

const saveToCache = async (invoices, userTel) => {
  try {
    const existingInvoices = await db.invoices.where('userTel').equals(userTel).toArray();
    for (const inv of existingInvoices) {
      await db.invoices.delete(inv.cacheId);
    }
    
    const invoicesWithId = invoices.map((inv, index) => ({ 
      ...inv, 
      cacheId: `${userTel}_${inv.id}_${index}`,
      userTel 
    }));
    
    if (invoicesWithId.length > 0) {
      const batchSize = 50;
      for (let i = 0; i < invoicesWithId.length; i += batchSize) {
        const batch = invoicesWithId.slice(i, i + batchSize);
        await db.invoices.bulkAdd(batch);
      }
    }
    await db.cacheInfo.put({ key: `invoices_data_${userTel}`, timestamp: new Date().toISOString(), userTel });
    console.log('Données sauvegardées dans le cache pour', userTel);
  } catch (error) {
    console.error('Erreur sauvegarde cache:', error);
  }
};

const loadFromCache = async (userTel) => {
  try {
    const invoices = await db.invoices.where('userTel').equals(userTel).toArray();
    return invoices;
  } catch (error) {
    console.error('Erreur chargement cache:', error);
    return [];
  }
};

const clearCache = async (userTel) => {
  try {
    const invoices = await db.invoices.where('userTel').equals(userTel).toArray();
    for (const inv of invoices) {
      await db.invoices.delete(inv.cacheId);
    }
    await db.cacheInfo.delete(`invoices_data_${userTel}`);
    console.log('Cache vidé pour', userTel);
  } catch (error) {
    console.error('Erreur vidage cache:', error);
  }
};

const formatCurrency = (amount, currency = "CDF") => {
  if (amount == null || isNaN(amount)) return "-";
  const formatter = new Intl.NumberFormat("fr-CD", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateCache = (dateString) => {
  if (!dateString) return 'Jamais';
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

// ========== STYLES GLASSMORPHISM ==========
const glassStyles = {
  card: "bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 shadow-[0_8px_32px_rgba(0,0,0,0.3)] hover:shadow-[0_8px_32px_rgba(59,130,246,0.15)] rounded-3xl transition-all duration-500",
  surface: "bg-white/[0.03] backdrop-blur-md border border-white/[0.06] rounded-2xl shadow-lg",
  input: "bg-white/[0.03] backdrop-blur-md border border-blue-400/20 rounded-2xl focus:border-blue-400/50 focus:ring-2 focus:ring-blue-400/20 transition-all duration-300",
  button: {
    primary: "bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300",
    secondary: "bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.08] text-slate-300 transition-all duration-300",
    danger: "bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 transition-all duration-300",
  }
};

// ========== CONSTANTES DE COULEURS ==========
const CHART_COLORS = {
  blue: '#3b82f6',
  emerald: '#10b981',
  cyan: '#06b6d4',
  violet: '#8b5cf6',
  amber: '#f59e0b',
  rose: '#f43f5e',
  indigo: '#6366f1',
  teal: '#14b8a6',
  fuchsia: '#d946ef',
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#06b6d4', '#8b5cf6', '#f59e0b', '#f43f5e', '#6366f1', '#ec4899'];

const COLOR_MAP = {
  blue: { text: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20", glow: "shadow-blue-500/25" },
  emerald: { text: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20", glow: "shadow-emerald-500/25" },
  cyan: { text: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/20", glow: "shadow-cyan-500/25" },
  violet: { text: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20", glow: "shadow-violet-500/25" },
  amber: { text: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20", glow: "shadow-amber-500/25" },
  rose: { text: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20", glow: "shadow-rose-500/25" },
  indigo: { text: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/20", glow: "shadow-indigo-500/25" },
};

// ========== COMPOSANTS RÉUTILISABLES ==========

// StatCard
const StatCard = ({ title, value, icon: Icon, color, format, trend, trendValue, delay = 0 }) => {
  const colorStyles = COLOR_MAP[color] || COLOR_MAP.blue;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 200, damping: 20 }}
      whileHover={{ y: -8, scale: 1.02 }}
      className={`${glassStyles.card} p-5 md:p-6 relative overflow-hidden cursor-pointer group`}
    >
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent -skew-x-12"
        initial={{ x: '-100%' }}
        whileHover={{ x: '200%' }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
      />
      
      <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-gradient-to-br from-blue-500/10 to-purple-500/10 blur-xl" />
      <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-gradient-to-tr from-cyan-500/10 to-emerald-500/10 blur-xl" />
      
      <div className="relative flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <p className="text-slate-400 text-xs md:text-sm font-medium tracking-wider uppercase">{title}</p>
          <motion.p 
            className={`text-xl md:text-3xl font-bold ${colorStyles.text}`}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: delay + 0.2, type: "spring", stiffness: 200 }}
          >
            {format(value)}
          </motion.p>
          {trend && (
            <motion.div 
              className="flex items-center space-x-1.5 text-xs"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: delay + 0.3 }}
            >
              <span className={`flex items-center ${trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
                {trend === 'up' ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
                {trendValue}%
              </span>
              <span className="text-slate-500 hidden sm:inline">vs mois dernier</span>
            </motion.div>
          )}
        </div>
        <motion.div 
          whileHover={{ rotate: 15, scale: 1.15 }}
          transition={{ type: "spring", stiffness: 300 }}
          className={`p-3 rounded-2xl ${colorStyles.bg} ${colorStyles.border} border backdrop-blur-sm`}
        >
          <Icon className={`text-lg md:text-xl ${colorStyles.text}`} />
        </motion.div>
      </div>
    </motion.div>
  );
};

// GlassChart
const GlassChart = ({ children, title, icon: Icon, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ type: "spring", stiffness: 150, damping: 20 }}
    className={`${glassStyles.card} p-5 md:p-6 ${className}`}
  >
    <div className="flex items-center space-x-3 mb-6">
      <div className="p-2 rounded-xl bg-white/[0.05] border border-white/[0.08]">
        <Icon className="text-blue-400 text-lg" />
      </div>
      <h3 className="text-lg font-semibold text-white tracking-wide">{title}</h3>
    </div>
    {children}
  </motion.div>
);

// InvoiceCard
const InvoiceCard = ({ invoice, onViewImage, index }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, y: -20 }}
      transition={{ delay: index * 0.05, type: "spring", stiffness: 200 }}
      layout
      className={`${glassStyles.surface} p-4 mb-3`}
    >
      <motion.div layout className="flex justify-between items-start mb-3">
        <div>
          <p className="text-slate-500 text-xs mb-1">#{invoice.id}</p>
          <p className="text-white font-medium text-sm">{invoice.company_name || 'N/A'}</p>
        </div>
        <div className="text-right">
          <p className="text-slate-500 text-xs mb-1">Total TTC</p>
          <p className="text-emerald-400 font-bold text-lg">{formatCurrency(invoice.total_ttc)}</p>
        </div>
      </motion.div>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 25 }}
            className="overflow-hidden"
          >
            <div className="space-y-2 pt-3 border-t border-white/[0.06]">
              <div className="flex items-center text-slate-400 text-xs">
                <FaEnvelope className="mr-2 text-slate-600 w-4" />
                <a href={`mailto:${invoice.email}`} className="text-blue-400 truncate">{invoice.email || '-'}</a>
              </div>
              <div className="flex items-center text-slate-400 text-xs">
                <FaMapMarkerAlt className="mr-2 text-slate-600 w-4" />
                <span className="truncate">{invoice.address || '-'}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white/[0.02] rounded-xl p-2">
                  <p className="text-slate-500">HT</p>
                  <p className="text-blue-400 font-mono">{formatCurrency(invoice.total_ht)}</p>
                </div>
                <div className="bg-white/[0.02] rounded-xl p-2">
                  <p className="text-slate-500">TVA</p>
                  <p className="text-cyan-400 font-mono">{formatCurrency(invoice.tax_amount)}</p>
                </div>
                <div className="bg-white/[0.02] rounded-xl p-2">
                  <p className="text-slate-500">Date</p>
                  <p className="text-slate-300 text-[10px]">{formatDate(invoice.created_at)}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="flex gap-2 mt-3 pt-3 border-t border-white/[0.06]">
        <motion.button
          onClick={() => setExpanded(!expanded)}
          whileTap={{ scale: 0.95 }}
          className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium transition-all duration-300 ${
            expanded ? 'bg-blue-500/20 text-blue-400' : 'bg-white/[0.03] text-slate-400'
          }`}
        >
          {expanded ? 'Moins d\'infos' : 'Plus d\'infos'}
        </motion.button>
        <motion.button
          disabled={!invoice.image_path}
          onClick={() => onViewImage(invoice.image_path)}
          whileTap={{ scale: 0.95 }}
          className="flex-1 px-3 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl text-xs font-medium flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-blue-500/20 transition-all"
        >
          <FaEye className="mr-1.5" />
          Facture
        </motion.button>
      </div>
    </motion.div>
  );
};

// MobileMenu
const MobileMenu = ({ isOpen, onClose, onRefresh, onClearCache, isRefreshing }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-80 bg-gradient-to-b from-blue-950/95 via-indigo-950/95 to-slate-900/95 backdrop-blur-xl z-50 shadow-2xl border-l border-blue-400/20"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="text-white font-bold text-lg">Menu</h3>
                  <p className="text-slate-400 text-xs">Actions rapides</p>
                </div>
                <motion.button 
                  onClick={onClose} 
                  whileHover={{ rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-2 rounded-xl bg-white/[0.05] text-slate-400 hover:text-white transition-colors"
                >
                  <FaTimes size={18} />
                </motion.button>
              </div>
              <div className="space-y-4">
                <motion.button
                  onClick={() => { onRefresh(); onClose(); }}
                  disabled={isRefreshing}
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full p-4 bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-500/20 rounded-2xl text-amber-400 text-left flex items-center space-x-3 disabled:opacity-50 hover:from-amber-500/20 hover:to-amber-600/20 transition-all"
                >
                  <FaSyncAlt className={isRefreshing ? 'animate-spin' : ''} />
                  <span>{isRefreshing ? 'Mise à jour...' : 'Actualiser les données'}</span>
                </motion.button>
                <motion.button
                  onClick={() => { onClearCache(); onClose(); }}
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full p-4 bg-gradient-to-r from-red-500/10 to-rose-500/10 border border-red-500/20 rounded-2xl text-red-400 text-left flex items-center space-x-3 hover:from-red-500/20 hover:to-rose-500/20 transition-all"
                >
                  <FaDatabase />
                  <span>Vider le cache</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// CircularProgress
const CircularProgress = ({ value, max, label, color }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const radius = 55;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const colorStyles = COLOR_MAP[color] || COLOR_MAP.blue;

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width="140" height="140" viewBox="0 0 140 140">
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={CHART_COLORS[color]} stopOpacity="0.8" />
              <stop offset="100%" stopColor={CHART_COLORS[color]} stopOpacity="1" />
            </linearGradient>
          </defs>
          <circle cx="70" cy="70" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
          <motion.circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            stroke={`url(#gradient-${color})`}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 2, ease: "easeInOut", delay: 0.3 }}
            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', filter: `drop-shadow(0 0 8px ${CHART_COLORS[color]}40)` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span 
            className={`text-3xl font-bold ${colorStyles.text}`}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.8, type: "spring" }}
          >
            {percentage.toFixed(0)}%
          </motion.span>
          <span className="text-slate-400 text-xs mt-1">du total</span>
        </div>
      </div>
      <p className="text-slate-300 text-sm mt-3 font-medium">{label}</p>
      <p className="text-slate-500 text-xs">{formatCurrency(value)}</p>
    </div>
  );
};

// ========== COMPOSANT PRINCIPAL ==========
export default function InvoiceTableADM({ userId, tel }) {
  const [invoices, setInvoices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalImage, setModalImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheDate, setCacheDate] = useState(null);
  const [rateLimitError, setRateLimitError] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  
  const lastFetchRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  const { scrollY } = useScroll();
  const headerOpacity = useTransform(scrollY, [0, 100], [1, 0.8]);
  const headerBlur = useTransform(scrollY, [0, 100], [0, 20]);

  // Détection mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const itemsPerPage = isMobile ? 5 : 10;

  // Surveillance de la connexion internet
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const debouncedFetch = useCallback((callback, delay = 1000) => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(callback, delay);
  }, []);

  /* FETCH API WITH CACHE ET RATE LIMITING */
  const fetchFactures = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh && lastFetchRef.current) {
        const timeSinceLastFetch = Date.now() - lastFetchRef.current;
        if (timeSinceLastFetch < 10000) {
          console.log('Requête trop récente, utilisation du cache');
          const cachedInvoices = await loadFromCache(tel);
          if (cachedInvoices && cachedInvoices.length > 0) {
            setInvoices(cachedInvoices);
            setFiltered(cachedInvoices);
            setLoading(false);
            return;
          }
        }
      }

      setLoading(true);
      setError(null);
      setRateLimitError(false);
      
      if (!forceRefresh) {
        const cacheValid = await isCacheValid(tel);
        if (cacheValid) {
          const cachedInvoices = await loadFromCache(tel);
          if (cachedInvoices && cachedInvoices.length > 0) {
            setInvoices(cachedInvoices);
            setFiltered(cachedInvoices);
            const cacheInfo = await db.cacheInfo.get(`invoices_data_${tel}`);
            if (cacheInfo) setCacheDate(cacheInfo.timestamp);
            if (isOnline && !forceRefresh) debouncedFetch(() => refreshDataInBackground(), 15000);
            setLoading(false);
            return;
          }
        }
      }
      
      await rateLimiter.acquire();
      await logRequest('AllFacturesADM.php', tel);
      
      const res = await axiosInstance.get(`${API_CONFIG.apiUrl}AllFacturesADM.php?tel=${tel}`, {
        _retry: true,
        _retryCount: 0,
        _retryDelay: (retryCount) => Math.min(1000 * Math.pow(2, retryCount), 10000)
      });
      
      if (!Array.isArray(res.data)) throw new Error("Le backend ne renvoie pas un tableau");
      
      lastFetchRef.current = Date.now();
      setInvoices(res.data);
      setFiltered(res.data);
      await saveToCache(res.data, tel);
      const cacheInfo = await db.cacheInfo.get(`invoices_data_${tel}`);
      if (cacheInfo) setCacheDate(cacheInfo.timestamp);
      
    } catch (err) {
      if (err.response && err.response.status === 429) {
        setRateLimitError(true);
        setError("Trop de requêtes. Veuillez patienter avant de réessayer.");
        setTimeout(() => { if (isOnline) fetchFactures(false); }, 30000);
      } else if (err.code === 'ECONNABORTED') {
        setError("Timeout de connexion. Vérifiez votre connexion internet.");
      } else {
        const cachedInvoices = await loadFromCache(tel);
        if (cachedInvoices && cachedInvoices.length > 0) {
          setInvoices(cachedInvoices);
          setFiltered(cachedInvoices);
          setError(null);
        } else if (!error) {
          setError("Erreur lors du chargement des données: " + (err.message || "Erreur inconnue"));
          console.error(err);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isOnline, tel, debouncedFetch]);

  const refreshDataInBackground = async () => {
    try {
      await rateLimiter.acquire();
      const res = await axiosInstance.get(`${API_CONFIG.apiUrl}AllFacturesADM.php?tel=${tel}`, {
        _retry: true,
        _retryCount: 0,
        _retryDelay: (retryCount) => Math.min(1000 * Math.pow(2, retryCount), 10000)
      });
      if (Array.isArray(res.data)) {
        setInvoices(res.data);
        setFiltered(res.data);
        await saveToCache(res.data, tel);
        lastFetchRef.current = Date.now();
      }
    } catch (err) {
      console.log('Background refresh failed:', err);
    }
  };

  const manualRefresh = async () => {
    if (!isOnline) {
      setError('Pas de connexion internet. Impossible de rafraîchir.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setError(null);
    setRateLimitError(false);
    
    try {
      await fetchFactures(true);
      const notification = document.createElement('div');
      notification.innerHTML = '✅ Données mises à jour avec succès !';
      notification.style.cssText = 'position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: #10b981; color: white; padding: 10px 20px; border-radius: 10px; z-index: 1000;';
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    } catch (err) {
      if (err.response && err.response.status === 429) {
        setError('Trop de requêtes. Veuillez patienter 30 secondes.');
      } else {
        setError('Erreur lors du rafraîchissement');
      }
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const clearCacheAndReload = async () => {
    await clearCache(tel);
    lastFetchRef.current = null;
    await fetchFactures(true);
  };

  useEffect(() => {
    fetchFactures();
    return () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); };
  }, [fetchFactures]);

  // Filtrage
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    searchTimeoutRef.current = setTimeout(() => {
      const q = search.toLowerCase().trim();
      let result = invoices;
      if (q) {
        result = invoices.filter((inv) => (
          (inv.description && inv.description.toLowerCase().includes(q)) ||
          (inv.company_name && inv.company_name.toLowerCase().includes(q)) ||
          (inv.email && inv.email.toLowerCase().includes(q)) ||
          (inv.address && inv.address.toLowerCase().includes(q))
        ));
      }
      setFiltered(result);
      setCurrentPage(1);
    }, 300);
    
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [search, invoices]);

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    
    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      
      if (['total_ht', 'tax_amount', 'total_ttc'].includes(key)) {
        return direction === 'asc' 
          ? (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0)
          : (parseFloat(bVal) || 0) - (parseFloat(aVal) || 0);
      }
      
      if (key === 'created_at') {
        return direction === 'asc'
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFiltered(sorted);
  };

  const stats = useMemo(() => {
    const totalHT = filtered.reduce((s, x) => s + (parseFloat(x.total_ht) || 0), 0);
    const totalTVA = filtered.reduce((s, x) => s + (parseFloat(x.tax_amount) || 0), 0);
    const totalTTC = filtered.reduce((s, x) => s + (parseFloat(x.total_ttc) || 0), 0);
    const previousMonthTotal = totalTTC * 0.85;
    const trend = ((totalTTC - previousMonthTotal) / previousMonthTotal * 100).toFixed(1);
    
    return { count: filtered.length, totalHT, totalTVA, totalTTC, trend: parseFloat(trend) };
  }, [filtered]);

  const pieData = useMemo(() => [
    { name: "Total HT", value: stats.totalHT },
    { name: "Total TVA", value: stats.totalTVA },
    { name: "Total TTC", value: stats.totalTTC },
  ], [stats]);

  const barData = useMemo(() => [
    { label: "HT", value: stats.totalHT },
    { label: "TVA", value: stats.totalTVA },
    { label: "TTC", value: stats.totalTTC },
  ], [stats]);

  const trendData = useMemo(() => {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return months.map((month, index) => ({ month, value: stats.totalTTC * (0.5 + (index / 24)) }));
  }, [stats.totalTTC]);

  const topCompanies = useMemo(() => {
    const companyMap = new Map();
    filtered.forEach(inv => {
      if (inv.company_name) {
        const current = companyMap.get(inv.company_name) || 0;
        companyMap.set(inv.company_name, current + (parseFloat(inv.total_ttc) || 0));
      }
    });
    return Array.from(companyMap.entries())
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [filtered]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const handlePrev = () => setCurrentPage((p) => (p > 1 ? p - 1 : p));
  const handleNext = () => setCurrentPage((p) => (p < totalPages ? p + 1 : p));

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") setModalImage(null); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 flex items-center justify-center">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center"
      >
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1],
          }}
          transition={{ 
            rotate: { duration: 2, repeat: Infinity, ease: "linear" },
            scale: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
          }}
          className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 p-1"
        >
          <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center">
            <FaSpinner className="text-3xl text-blue-400" />
          </div>
        </motion.div>
        <motion.p
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-slate-300 text-lg"
        >
          Chargement des factures...
        </motion.p>
      </motion.div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 flex items-center justify-center p-4">
      <motion.div 
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={`${glassStyles.card} p-8 max-w-md w-full text-center`}
      >
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-red-400 text-6xl mb-6"
        >
          <FaExclamationTriangle className="mx-auto" />
        </motion.div>
        <h3 className="text-white text-xl font-semibold mb-2">Erreur de chargement</h3>
        <p className="text-slate-400 mb-6">{error}</p>
        <div className="flex gap-3 justify-center">
          <motion.button
            onClick={manualRefresh}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`${glassStyles.button.primary} px-6 py-3 rounded-xl font-medium`}
          >
            Réessayer
          </motion.button>
          {rateLimitError && (
            <motion.button
              onClick={clearCacheAndReload}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`${glassStyles.button.secondary} px-6 py-3 rounded-xl font-medium`}
            >
              Utiliser le cache
            </motion.button>
          )}
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 relative">
      {/* Fond décoratif animé */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto p-3 md:p-6 pb-24 md:pb-6">
        
        {/* HEADER */}
        <motion.div
          style={{ opacity: headerOpacity, backdropFilter: `blur(${headerBlur}px)` }}
          className={`${glassStyles.card} p-4 md:p-6 mb-4 md:mb-8 sticky top-3 md:top-6 z-20`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <motion.div
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className="flex items-center space-x-3"
              >
                <div className="p-2.5 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-400/20">
                  <FaReceipt className="text-blue-400 text-xl md:text-2xl" />
                </div>
                <div>
                  <h1 className="text-xl md:text-3xl font-bold text-white tracking-tight">
                    Factures ADM
                  </h1>
                  <p className="text-slate-400 text-sm mt-1">
                    {filtered.length} facture{filtered.length > 1 ? 's' : ''} · Mis à jour {formatDateCache(cacheDate)}
                  </p>
                </div>
              </motion.div>
            </div>
            
            <div className="flex items-center gap-3">
              <motion.button
                onClick={() => setShowFilters(!showFilters)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-2.5 rounded-xl ${showFilters ? 'bg-blue-500/20 border-blue-500/30' : 'bg-white/[0.03] border-white/[0.06]'} border transition-all`}
              >
                <FaFilter className={`text-lg ${showFilters ? 'text-blue-400' : 'text-slate-400'}`} />
              </motion.button>
              
              <motion.button
                onClick={manualRefresh}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={isRefreshing}
                className={`${glassStyles.button.primary} px-4 py-2.5 rounded-xl flex items-center space-x-2 text-sm font-medium disabled:opacity-50`}
              >
                <FaSyncAlt className={isRefreshing ? 'animate-spin' : ''} />
                <span className="hidden md:inline">{isRefreshing ? 'Actualisation...' : 'Actualiser'}</span>
              </motion.button>
              
              <motion.button
                onClick={() => setMobileMenuOpen(true)}
                whileTap={{ scale: 0.9 }}
                className="md:hidden p-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08]"
              >
                <FaBars className="text-slate-300" />
              </motion.button>
            </div>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="relative mt-4">
                  <FaSearch className="absolute left-4 top-3.5 text-slate-500" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={`${glassStyles.input} pl-12 pr-4 py-3 w-full text-white text-sm placeholder-slate-500`}
                    placeholder="Rechercher par société, email, adresse..."
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
          <StatCard
            title="Total Factures"
            value={stats.count}
            icon={FaListAlt}
            color="blue"
            format={(val) => val.toLocaleString()}
            trend="up"
            trendValue="12"
            delay={0}
          />
          <StatCard
            title="Total HT"
            value={stats.totalHT}
            icon={FaFileInvoiceDollar}
            color="emerald"
            format={(val) => formatCurrency(val)}
            trend="up"
            trendValue="8"
            delay={0.1}
          />
          <StatCard
            title="Total TVA"
            value={stats.totalTVA}
            icon={FaPercent}
            color="cyan"
            format={(val) => formatCurrency(val)}
            trend="down"
            trendValue="3"
            delay={0.2}
          />
          <StatCard
            title="Total TTC"
            value={stats.totalTTC}
            icon={FaMoneyBill}
            color="violet"
            format={(val) => formatCurrency(val)}
            trend="up"
            trendValue={stats.trend}
            delay={0.3}
          />
        </div>

        {/* GRAPHIQUES */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
          <GlassChart title="Distribution" icon={FaChartPie}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie 
                  data={pieData} 
                  cx="50%" 
                  cy="50%" 
                  innerRadius={60} 
                  outerRadius={100} 
                  paddingAngle={8} 
                  dataKey="value"
                  animationBegin={0}
                  animationDuration={1500}
                >
                  {pieData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={PIE_COLORS[index % PIE_COLORS.length]} 
                      stroke="rgba(0,0,0,0.3)" 
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    background: 'rgba(15, 23, 42, 0.95)', 
                    borderRadius: '16px', 
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                    backdropFilter: 'blur(10px)',
                    color: 'white' 
                  }} 
                  formatter={(value) => [formatCurrency(value), 'Montant']} 
                />
                <Legend 
                  formatter={(value) => <span className="text-slate-300 text-sm">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </GlassChart>
          
          <GlassChart title="Comparaison" icon={FaChartBar}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" />
                <YAxis stroke="rgba(255,255,255,0.4)" tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'rgba(15, 23, 42, 0.95)', 
                    borderRadius: '16px', 
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                    color: 'white' 
                  }} 
                  formatter={(value) => [formatCurrency(value), 'Montant']} 
                />
                <Bar dataKey="value" radius={[10, 10, 0, 0]} animationBegin={0} animationDuration={1500}>
                  {barData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </GlassChart>
        </div>

        {/* SECTION ÉVOLUTION ET TOP ENTREPRISES */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
          <GlassChart title="Évolution mensuelle" icon={FaChartLine}>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.6}/>
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" stroke="rgba(255,255,255,0.4)" />
                <YAxis stroke="rgba(255,255,255,0.4)" tickFormatter={(value) => formatCurrency(value)} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'rgba(15, 23, 42, 0.95)', 
                    borderRadius: '16px', 
                    border: '1px solid rgba(148, 163, 184, 0.1)',
                    color: 'white' 
                  }} 
                  formatter={(value) => [formatCurrency(value), 'Montant TTC']} 
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#06b6d4" 
                  strokeWidth={2} 
                  fill="url(#areaGradient)" 
                  animationBegin={0} 
                  animationDuration={2000} 
                />
              </AreaChart>
            </ResponsiveContainer>
          </GlassChart>

          <GlassChart title="Indicateurs de performance" icon={FaTachometerAlt}>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <CircularProgress value={stats.totalHT} max={stats.totalTTC} label="HT" color="emerald" />
              <CircularProgress value={stats.totalTVA} max={stats.totalTTC} label="TVA" color="cyan" />
              <CircularProgress value={stats.totalTTC} max={stats.totalTTC} label="TTC" color="violet" />
            </div>
            <div className="border-t border-white/[0.06] pt-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-4">Top 5 entreprises</h4>
              {topCompanies.map((company, index) => (
                <motion.div
                  key={company.name}
                  className="flex justify-between items-center py-2.5 border-b border-white/[0.03]"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-center space-x-2">
                    <span className="w-6 h-6 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-xs text-blue-400">
                      {index + 1}
                    </span>
                    <span className="text-slate-300 text-sm">{company.name}</span>
                  </div>
                  <span className="text-emerald-400 font-mono text-sm">{formatCurrency(company.amount)}</span>
                </motion.div>
              ))}
            </div>
          </GlassChart>
        </div>

        {/* TABLEAU DES FACTURES */}
        <GlassChart title="Factures récentes" icon={FaLayerGroup} className="mb-4 md:mb-8">
          {/* Version mobile - Cartes */}
          <div className="block md:hidden space-y-3">
            <AnimatePresence>
              {currentItems.length > 0 ? currentItems.map((inv, i) => (
                <InvoiceCard
                  key={inv.id || i}
                  invoice={inv}
                  onViewImage={setModalImage}
                  index={i}
                />
              )) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-8"
                >
                  <FaSearch className="text-slate-600 text-4xl mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Aucune facture trouvée</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Version desktop - Tableau */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {[
                    { key: 'id', label: 'ID' },
                    { key: 'company_name', label: 'Société' },
                    { key: 'email', label: 'Email' },
                    { key: 'total_ht', label: 'HT' },
                    { key: 'tax_amount', label: 'TVA' },
                    { key: 'total_ttc', label: 'TTC' },
                    { key: 'created_at', label: 'Date' },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      onClick={() => handleSort(key)}
                      className="p-4 text-left text-slate-400 text-sm font-medium cursor-pointer hover:text-white transition-colors group"
                    >
                      <div className="flex items-center space-x-1">
                        <span>{label}</span>
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                          {sortConfig.key === key ? (
                            sortConfig.direction === 'asc' ? <FaSortAmountUp className="text-xs" /> : <FaSortAmountDown className="text-xs" />
                          ) : (
                            <FaSortAmountDown className="text-xs text-slate-600" />
                          )}
                        </span>
                      </div>
                    </th>
                  ))}
                  <th className="p-4 text-center text-slate-400 text-sm">Action</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {currentItems.length > 0 ? currentItems.map((inv, i) => (
                    <motion.tr
                      key={inv.id || i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: i * 0.03 }}
                      className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors group"
                    >
                      <td className="p-4">
                        <span className="text-blue-400 font-mono text-sm">#{inv.id}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <FaBuilding className="text-slate-600 text-xs" />
                          <span className="text-slate-300 text-sm">{inv.company_name || '-'}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <a href={`mailto:${inv.email}`} className="text-blue-400 hover:text-blue-300 text-sm transition-colors">
                          {inv.email || '-'}
                        </a>
                      </td>
                      <td className="p-4 text-blue-400 font-mono text-sm">{formatCurrency(inv.total_ht)}</td>
                      <td className="p-4 text-cyan-400 font-mono text-sm">{formatCurrency(inv.tax_amount)}</td>
                      <td className="p-4 text-emerald-400 font-bold font-mono text-sm">{formatCurrency(inv.total_ttc)}</td>
                      <td className="p-4 text-slate-400 text-sm">
                        <div className="flex items-center space-x-2">
                          <FaCalendar className="text-slate-600 text-xs" />
                          <span>{formatDate(inv.created_at)}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <motion.button
                          disabled={!inv.image_path}
                          onClick={() => setModalImage(inv.image_path)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl text-sm font-medium hover:bg-blue-500/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <FaEye className="inline mr-1" />
                          Voir
                        </motion.button>
                      </td>
                    </motion.tr>
                  )) : (
                    <tr>
                      <td colSpan={8} className="p-8 text-center">
                        <FaSearch className="text-slate-600 text-4xl mx-auto mb-3" />
                        <p className="text-slate-400 text-sm">Aucune facture trouvée</p>
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6 pt-6 border-t border-white/[0.06]">
              <motion.button
                onClick={handlePrev}
                disabled={currentPage === 1}
                whileHover={{ scale: currentPage === 1 ? 1 : 1.05 }}
                whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
                className={`p-2 rounded-xl ${currentPage === 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/[0.05]'} transition-all`}
              >
                <FaChevronLeft className="text-slate-400" />
              </motion.button>
              
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <motion.button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-10 h-10 rounded-xl text-sm font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : 'text-slate-400 hover:bg-white/[0.05]'
                      }`}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
              </div>
              
              <motion.button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                whileHover={{ scale: currentPage === totalPages ? 1 : 1.05 }}
                whileTap={{ scale: currentPage === totalPages ? 1 : 0.95 }}
                className={`p-2 rounded-xl ${currentPage === totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/[0.05]'} transition-all`}
              >
                <FaChevronRight className="text-slate-400" />
              </motion.button>
            </div>
          )}
        </GlassChart>
      </div>

      {/* MENU MOBILE */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onRefresh={manualRefresh}
        onClearCache={clearCacheAndReload}
        isRefreshing={isRefreshing}
      />

      {/* MODAL IMAGE */}
      <AnimatePresence>
        {modalImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-xl flex items-center justify-center z-50 p-4"
            onClick={() => setModalImage(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative max-w-5xl w-full max-h-[90vh]"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.button
                className="absolute -top-12 right-0 text-white/80 hover:text-white text-2xl font-light w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-all"
                onClick={() => setModalImage(null)}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                &times;
              </motion.button>
              <img
                src={modalImage ? `${API_CONFIG.apiUrl}/${modalImage}` : ""}
                alt="Facture"
                className="w-full h-auto max-h-[85vh] object-contain rounded-2xl shadow-2xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23111'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-family='Arial' font-size='16'%3EImage non disponible%3C/text%3E%3C/svg%3E";
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* INDICATEUR DE CONNEXION */}
      <motion.div
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-full text-xs font-medium flex items-center space-x-2 backdrop-blur-xl border ${
          isOnline 
            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
            : 'bg-red-500/10 text-red-400 border-red-500/20'
        }`}
      >
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
        <span>{isOnline ? 'En ligne' : 'Hors ligne'}</span>
      </motion.div>

      {/* BARRE DE RECHERCHE MOBILE */}
      <div className="block md:hidden fixed bottom-0 left-0 right-0 p-3 bg-slate-900/95 backdrop-blur-xl border-t border-white/[0.08] z-30">
        <div className="relative">
          <FaSearch className="absolute left-4 top-3 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 bg-white/[0.03] rounded-2xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            placeholder="Rechercher une facture..."
          />
        </div>
      </div>
    </div>
  );
}